import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import BlogModal from './BlogModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';
import { BlogType } from "../../../../types/BlogType";
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));


const Blogs: React.FC = () => {
  const [blogs, setBlogs] = useState<BlogType[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<BlogType | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchBlogs = async () => {
    try {
      const data = await apiRequest<BlogType[]>({
        method: 'get',
        url: 'blogs',
      });
      setBlogs(data);
    } catch (error) {
      showError('Failed to fetch blog');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const handleEditClick = (record: BlogType) => {
    setIsEditMode(true);
    setInitialValues({
      blog_id: record.blog_id,
      blog_title: record.blog_title,
      blog_description: record.blog_description,
      blog_short_description: record.blog_short_description,
      blog_status: record.blog_status,
      blog_image: record.blog_image,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchBlogs();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (blog_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-blog/${blog_id}`,
      });
      showSuccess('Blog deleted');
      fetchBlogs();
    } catch {
      showError('Failed to delete blog');
    }
  };

  const handleStatusToggle = async (blogId: number) => {
    setBlogs((prev) =>
      prev.map((item) =>
        item.blog_id === blogId
          ? { ...item, blog_status: item.blog_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-blog/${blogId}`,
      });
      showSuccess('Blog status updated');
    } catch (error) {
      showError('Failed to update status');
      setBlogs((prev) =>
        prev.map((item) =>
          item.blog_id === blogId
            ? { ...item, blog_status: item.blog_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<BlogType>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<BlogType> = [
    
    {
      title: 'Title',
      dataIndex: 'blog_title',
      key: 'blog_title',
      width: 250,
      filters: Array.from(new Set(blogs.map((s) => s.blog_title))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.blog_title || null,
      onFilter: (value, record) => record.blog_title.includes(value as string),
      sorter: (a, b) => a.blog_title.localeCompare(b.blog_title),
      sortOrder: sortedInfo.columnKey === 'blog_title' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div className='text-wrap'>{record.blog_title}</div>,
    },
    {
      title: 'Short Description',
      dataIndex: 'blog_short_description',
      key: 'blog_short_description',
      width: 250,
      filters: Array.from(new Set(blogs.map((s) => s.blog_short_description))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.blog_short_description || null,
      onFilter: (value, record) => record.blog_short_description.includes(value as string),
      sorter: (a, b) => a.blog_short_description.localeCompare(b.blog_short_description),
      sortOrder: sortedInfo.columnKey === 'blog_short_description' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div className='text-wrap'>{record.blog_short_description}</div>,
    },
    {
      title: 'Description',
      dataIndex: 'blog_description',
      key: 'blog_description',
      width: 350,
      filters: Array.from(new Set(blogs.map((s) => s.blog_description))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.blog_description || null,
      onFilter: (value, record) => record.blog_description.includes(value as string),
      sorter: (a, b) => a.blog_description.localeCompare(b.blog_description),
      sortOrder: sortedInfo.columnKey === 'blog_description' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div className='text-wrap'>{record.blog_description}</div>,
    },
    {
      title: 'Image',
      dataIndex: 'blog_image',
      key: 'blog_image',
      width: 150,
      render: (_: any, record: any) =>
        record ? (
          <img
            src={record.blog_image}
            alt="blog"
            style={{
              width: 40,
              height: 50,
              objectFit: 'cover',
              borderRadius: 8,
            }}
          />
        ) : (
          '-'
        ),

    }
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 150,
      fixed: 'right',
      render: (_: any, record: BlogType) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.blog_status}
                onChange={() => handleStatusToggle(record.blog_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this blog?"
              onConfirm={() => handleDelete(record.blog_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View blog</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add blog 
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<BlogType>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="blog_id"
          dataSource={blogs.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          // scroll={{ y: 37 * 13 }}
           scroll={{ x: 1400, y: 37 * 13 }}
          locale={{ emptyText: 'No blog found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, blogs.length)} of{' '}
                {blogs.length}
              </span>
              <Pagination
                {...pagination}
                total={blogs.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <BlogModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
        blogs={blogs}
      />
    </>
  );
};

export default Blogs;